import React, { Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { IntlProvider } from 'react-intl'
import { Provider } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { useAsync, useCookie } from 'react-use'
import { Alert, LinearProgress } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'

import { YupErrorMapProvider } from '@lib/forms'
import { vantageTheme } from '@lib/theme'
import { config } from '@lib/ui-config'

import { loadLibraryMessages } from '../i18n/generated/libraryMessages'
import { buildAppMessages } from '../i18n/messageCatalog'
import type { Configuration } from '../redux/slices/configurationSlice'
import { updateConfig } from '../redux/slices/configurationSlice'
import { setupStore } from '../redux/store'
import { routes } from '../routes'

type Messages = Record<string, string>

/**
 * Library messages merge under the app's own, so app keys win collisions. A failed library
 * catalog degrades to the app's own messages instead of failing the whole load.
 */
const loadMessages = async (): Promise<Messages> => {
  let appMessages: Messages

  try {
    appMessages = buildAppMessages()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to build the message catalog:', error)

    throw error
  }

  try {
    const libraryMessages = await loadLibraryMessages()

    return { ...libraryMessages, ...appMessages }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load library messages:', error)

    return appMessages
  }
}

/**
 * The catalog is what failed, so nothing is left to translate with and the copy is hardcoded
 * English rather than an `IntlProvider` string.
 */
const AppLoadError = () => <Alert severity="error">We could not load this page. Refresh to try again.</Alert>

declare global {
  interface Window {
    config: any
  }
}

if (!window.config) {
  window.config = {
    ...config,
  }
}

export type AppProps = {
  publicPath: string | undefined
}

export function App({ publicPath }: AppProps) {
  const [csrfToken] = useCookie('csrftoken')
  const { value: messages, error, loading } = useAsync(loadMessages, [])

  if (loading) {
    return <LinearProgress />
  }

  if (error || !messages) {
    return <AppLoadError />
  }

  const configurationType: Configuration = {
    publicPath,
    csrfToken: csrfToken || '',
  }

  const store = setupStore()
  store.dispatch(updateConfig(configurationType))

  const router = createBrowserRouter(routes)

  return (
    <HelmetProvider>
      <IntlProvider locale="en" messages={messages}>
        <YupErrorMapProvider>
          <ThemeProvider theme={vantageTheme}>
            <Provider store={store}>
              <Suspense fallback={<LinearProgress />}>
                <RouterProvider router={router} />
              </Suspense>
            </Provider>
          </ThemeProvider>
        </YupErrorMapProvider>
      </IntlProvider>
    </HelmetProvider>
  )
}

export default App
